import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../../services/api';
import { useSnackbar } from '../../../components/common/Snackbar';
import { formatQueryType } from '../../../utils/assignedQueriesUtils';
import {
  buildProjectFilterOptionsFromApi,
  formatQueryProjectNameForDisplay,
} from '../../../utils/projectsUtils';


/**
 * Hook to manage query data fetching and processing for AssignedQueries page
 * @param {Object} params - Hook parameters
 * @param {string} params.searchQuery - Search query string
 * @param {Object} params.filters - Filter object
 * @param {Object} params.currentUser - Current user object
 * @param {Function} params.setAllQueries - Setter for all queries
 * @param {Function} params.setLoading - Setter for loading state
 * @param {Function} params.setProjectOptions - Setter for project options
 * @param {Function} params.setAssignedFilterOptions - Setter for assigned filter options
 * @param {Function} params.setQueryTypeOptions - Setter for query type options
 * @param {Function} params.setStatusSummary - Setter for status summary
 * @param {Function} params.setCurrentUser - Setter for current user
 * @param {Function} params.setCurrentUserRole - Setter for current user role
 * @param {Function} params.setCurrentUserRegion - Setter for current user region
 * @returns {Function} Fetch queries function
 */
export const useAssignedQueriesPageData = ({
  searchQuery,
  filters,
  currentUser,
  setAllQueries,
  setLoading,
  setProjectOptions,
  setAssignedFilterOptions,
  setQueryTypeOptions,
  setStatusSummary,
  setBackendCounts,
  setCurrentUser,
  setCurrentUserRole,
  setCurrentUserRegion,
}) => {
  const { showSnackbar } = useSnackbar();
  const projectsCatalogRef = useRef([]);
  const [projectsCatalog, setProjectsCatalog] = useState([]);

  // Fetch projects for filters
  useEffect(() => {
    const fetchProjectsForFilters = async () => {
      try {
        const response = await api.get('/projects', {
          params: {
            limit: 1000,
          },
        });
        const projectsData =
          response.data?.data?.projects ||
          response.data?.projects ||
          [];
        const list = Array.isArray(projectsData) ? projectsData : [];
        projectsCatalogRef.current = list;
        setProjectsCatalog(list);
        setProjectOptions(buildProjectFilterOptionsFromApi(list));
      } catch (error) {
        // console.error('Failed to load projects for filters:', error);
      }
    };

    fetchProjectsForFilters();
  }, [setProjectOptions]);

  // Fetch ED users by region
  useEffect(() => {
    const fetchEdUsers = async () => {
      try {
        const response = await api.get('/auth/ed-users-by-region');
        if (response.data?.success && response.data?.data?.edByRegion) {
          // This will be handled by the state hook
        }
      } catch (error) {
        // console.error('Failed to load ED users:', error);
      }
    };

    fetchEdUsers();
  }, []);

  const fetchQueries = useCallback(async () => {
    setLoading(true);
    try {
      // Get current user from localStorage
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        showSnackbar('User not found. Please login again.', 'error');
        setLoading(false);
        return;
      }

      const parsedUser = JSON.parse(userStr);
      const currentUserId = parsedUser.id || parsedUser._id;
      const role = parsedUser.role || '';
      const region = parsedUser.region || '';
      setCurrentUser(parsedUser);
      setCurrentUserRole((prev) => (prev === role ? prev : role));
      // Placeholder until the response comes back below - a user with
      // multiple posts across regions (see PostSelector) may have their
      // *active post's* region differ from this static account-level one.
      setCurrentUserRegion((prev) => (prev === region ? prev : region));

      // Fetch a larger set to enable region filtering, then paginate client-side
      const params = {
        page: 1,
        limit: 1000, // Fetch more to enable region filtering
      };

      // Add search parameter if present
      if (searchQuery && searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      // Add filters
      if (filters.status) params.status = filters.status;
      if (filters.projectName) params.projectName = filters.projectName;
      if (filters.queryType) params.queryType = filters.queryType;

      const response = await api.get('/queries/assigned', { params });

      // ── Backend counts (single source of truth for stat cards) ──────────────
      const assignedCounts = response.data?.counts?.assignedQueries || null;
      if (setBackendCounts) setBackendCounts(assignedCounts);
      // ────────────────────────────────────────────────────────────────────────

      // For ED/GM, the backend scopes counts to exactly one region key - the
      // *active post's* region (see postScopedRegionCode in queries.js), which
      // can differ from the account's static `region` above when a user holds
      // posts across multiple regions. Prefer it so the region card grid
      // reflects whichever post is currently active, not just the home region.
      const scopedRegionKeys = assignedCounts ? Object.keys(assignedCounts) : [];
      if (scopedRegionKeys.length === 1 && scopedRegionKeys[0] !== region) {
        setCurrentUserRegion(scopedRegionKeys[0]);
      }

      let allQueries = response.data.data.queries || [];
      // Client-side fallback search
      const trimmedSearch = (searchQuery || '').trim().toLowerCase();
      if (trimmedSearch) {
        allQueries = allQueries.filter((q) => {
          const id = (q.queryId || q._id || '').toString().toLowerCase();
          const header = (q.queryHeader || '').toLowerCase();
          const desc = (q.queryDescription || '').toLowerCase();
          const project = (q.projectName || '').toLowerCase();
          return (
            id.includes(trimmedSearch) ||
            header.includes(trimmedSearch) ||
            desc.includes(trimmedSearch) ||
            project.includes(trimmedSearch)
          );
        });
      }

      // If admin, show all queries. Otherwise, filter by assignee
      let filteredQueries;
      if (role === 'admin') {
        filteredQueries = allQueries.filter((query) => query.assignee?._id || query.assignee?.id || query.assignee);
      } else {
        // Filter queries to show only those assigned to current user
        // Include queries where user is current assignee, original assignee, or was ever in assignment history
        filteredQueries = allQueries.filter(query => {
          const assigneeId =
            query.assignee?._id ||
            query.assignee?.id ||
            (typeof query.assignee === 'string' ? query.assignee : null);
          const assignorId =
            query.assignor?._id ||
            query.assignor?.id ||
            (typeof query.assignor === 'string' ? query.assignor : null);
          const originalAssigneeId =
            query.originalAssignee?._id ||
            query.originalAssignee?.id ||
            (typeof query.originalAssignee === 'string' ? query.originalAssignee : null);
          const isAssigned =
            assigneeId && assigneeId.toString() === currentUserId?.toString();
          const isOriginalAssignee =
            originalAssigneeId && originalAssigneeId.toString() === currentUserId?.toString();

          // Check if user was ever in the assignment history (to show queries they delegated)
          let wasInAssignmentHistory = false;
          if (Array.isArray(query.assignmentHistory) && query.assignmentHistory.length > 0) {
            wasInAssignmentHistory = query.assignmentHistory.some(historyEntry => {
              const historyAssigneeId =
                historyEntry.assignee?._id ||
                historyEntry.assignee?.id ||
                (typeof historyEntry.assignee === 'string' ? historyEntry.assignee : null);
              return historyAssigneeId && historyAssigneeId.toString() === currentUserId?.toString();
            });
          }

          const includeQuery =
            isAssigned ||
            isOriginalAssignee ||
            wasInAssignmentHistory;
          return includeQuery;
        });
      }

      const assigneeMap = new Map();
      let includeUnassigned = false;
      filteredQueries.forEach((query) => {
        const assignee = query?.assignee;
        if (assignee && (assignee._id || assignee.id)) {
          const id = assignee._id || assignee.id;
          const name =
            assignee.name ||
            assignee.fullName ||
            assignee.username ||
            'Unnamed';
          if (!assigneeMap.has(id)) {
            assigneeMap.set(id, name);
          }
        } else {
          includeUnassigned = true;
        }
      });
      const assigneeOptions = [];
      if (includeUnassigned) {
        assigneeOptions.push({ value: 'unassigned', label: 'Unassigned' });
      }
      const sortedAssignees = Array.from(assigneeMap.entries())
        .sort((a, b) =>
          (a[1] || '').localeCompare(b[1] || '', undefined, {
            sensitivity: 'base',
          })
        )
        .map(([value, label]) => ({ value, label }));
      assigneeOptions.push(...sortedAssignees);
      setAssignedFilterOptions(assigneeOptions);

      const dynamicQueryTypes = new Set(
        filteredQueries
          .map((query) => query?.queryType)
          .filter((type) => typeof type === 'string' && type.trim() !== '')
      );
      setQueryTypeOptions((prev) => {
        const merged = [...prev];
        dynamicQueryTypes.forEach((type) => {
          const formattedLabel = formatQueryType(type);
          const exists = merged.some(
            (option) => option.label.toLowerCase() === formattedLabel.toLowerCase()
          );
          if (!exists) {
            merged.push({
              value: formattedLabel,
              label: formattedLabel,
            });
          }
        });
        return merged
          .slice()
          .sort((a, b) =>
            (a.label || '').localeCompare(b.label || '', undefined, {
              sensitivity: 'base',
            })
          );
      });

      let processedQueries = [...filteredQueries];
      if (filters.status) {
        processedQueries = processedQueries.filter(
          (query) => (query.status || '').toLowerCase() === filters.status.toLowerCase()
        );
      }

      if (filters.projectName) {
        processedQueries = processedQueries.filter(
          (query) =>
            (query.projectName || '').toString().toLowerCase() ===
            filters.projectName.toString().toLowerCase()
        );
      }

      if (filters.queryType) {
        processedQueries = processedQueries.filter(
          (query) =>
            (query.queryType || '').toString().toLowerCase() ===
            filters.queryType.toString().toLowerCase()
        );
      }

      if (filters.assignedTo) {
        if (filters.assignedTo === 'unassigned') {
          processedQueries = processedQueries.filter(
            (query) => !query.assignee || !(query.assignee._id || query.assignee.id)
          );
        } else {
          processedQueries = processedQueries.filter((query) => {
            const assigneeId =
              query.assignee?._id ||
              query.assignee?.id ||
              (typeof query.assignee === 'string' ? query.assignee : null);
            return (
              assigneeId &&
              assigneeId.toString().toLowerCase() === filters.assignedTo.toString().toLowerCase()
            );
          });
        }
      }

      setAllQueries(processedQueries);
      // Status summary is now driven by backend counts via buildRegionStatusSummary.
      // The setStatusSummary call below is intentionally omitted to prevent UI overrides.

      const catalog = projectsCatalogRef.current || [];
      const projectNameMap = new Map();
      processedQueries.forEach((query) => {
        const projectName = (query.projectName || '').toString().trim();
        if (!projectName) return;
        const key = projectName.toLowerCase();
        if (!projectNameMap.has(key)) {
          projectNameMap.set(key, {
            value: projectName,
            label: formatQueryProjectNameForDisplay(projectName, catalog),
          });
        }
      });
      setProjectOptions((prev) => {
        const merged = new Map();
        (Array.isArray(prev) ? prev : []).forEach((option) => {
          if (!option) return;
          const value = (option.value || option.label || '').toString().trim();
          if (!value) return;
          const key = value.toLowerCase();
          if (!merged.has(key)) {
            merged.set(key, {
              value: option.value || option.label,
              label: option.label || option.value,
            });
          }
        });
        projectNameMap.forEach((option, key) => {
          if (!merged.has(key)) {
            merged.set(key, option);
          }
        });
        return Array.from(merged.values()).sort((a, b) =>
          (a.label || '').localeCompare(b.label || '', undefined, {
            sensitivity: 'base',
          })
        );
      });
    } catch (error) {
      // console.error('Error fetching queries:', error);
      showSnackbar('Failed to load queries', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, searchQuery, setAllQueries, setLoading, setProjectOptions, setAssignedFilterOptions, setQueryTypeOptions, setStatusSummary, setBackendCounts, setCurrentUser, setCurrentUserRole, setCurrentUserRegion, showSnackbar]);

  // Fetch queries when search or filters change
  useEffect(() => {
    fetchQueries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, filters]);

  return {
    fetchQueries,
    projectsCatalog,
  };
};
